import { Quote } from '@brenger/api-client';
import { getLocation, push } from 'connected-react-router';
import { store } from '../..';
import { r } from '../../routes';
import { RootState, StopType } from '../../types';
import { logError, logException } from '../../utils/basics';
import { delay } from '../../utils/global';
import { translate } from '../../utils/localization';
import { priceClient } from '../../utils/request';
import { getAccountHasBusinessFlow, isUserLoggedIn } from '../User/ducks';
import {
  actions,
  getDifferentDeliveryDate,
  getInitialDatePath,
  getIsDateSelectionNotAvailable,
  getIsFlexDates,
  getIsFlexDatesModalActive,
  getIsMarketplaceUtmSource,
  getPickupSituation,
  getPriceRequestParams,
  getProductPaymentOptIn,
  getQuote,
  getSkipTimeOptions,
  isFloorServiceAvailable,
} from './ducks';
import { setSentryContext } from '../../utils/sentry';
import { GfChoicesKey } from './typings';
import * as Sentry from '@sentry/react';

interface ProceedAction {
  path?: string;
  showTimeModal?: boolean;
  showFlexDatesModal?: boolean;
  missingChoice?: GfChoicesKey;
}

interface Proceed {
  pathnameOverwrite?: string | null;
  withDelay?: boolean;
  updatePriceFirst?: boolean;
}

/**
 * Wrapper function that gets the proceed action and executes it.
 */
export const proceed = async (args?: Proceed): Promise<ProceedAction | null> => {
  if (args?.updatePriceFirst) {
    const quote = await fetchPrice();
    if (quote) store.dispatch(actions.setQuote(quote));
  }
  const state = store.getState() as RootState;
  const proceedAction = getProceedAction(state, args?.pathnameOverwrite);
  // Wait for animation to end?
  if (args?.withDelay !== false) await delay();
  // Execute next path
  if (proceedAction?.path) store.dispatch(push(proceedAction.path));
  if (proceedAction?.showTimeModal) store.dispatch(actions.setShowTimeModal(true));
  if (proceedAction?.showFlexDatesModal) store.dispatch(actions.setShowFlexDatesModal(true));
  if (proceedAction?.missingChoice)
    store.dispatch(actions.pushNotification(translate('request_flow.errors.select_required')));
  else {
    store.dispatch(actions.pushNotification(null));
  }
  // Bad news when proceedAction is null, so best to report it to Sentry.
  if (proceedAction === null) {
    const { router, generalTransport } = state;
    logException('Proceed function called without correct outcome', {
      router,
      generalTransport,
    });
  }
  // Return what we did so we can determine if succeeded
  return proceedAction;
};

const fetchPrice = async (): Promise<Quote | null> => {
  const state = store.getState() as RootState;
  let result: Quote | null = null;
  try {
    store.dispatch(actions.setPriceLoading(true));
    const params = getPriceRequestParams(state);
    if (params) result = await priceClient.quotes.create(params);
  } catch {
    logError('Pricing failed to update during proceed function');
  }
  store.dispatch(actions.setPriceLoading(false));
  return result;
};

/**
 * getProceedAction selector
 * Basically a big switch statement that returns the next path
 * Some cases need "more state" than current path, then it reaches for another sub selector
 * - Accepts a pathname overwrite so it is easy to test
 * - Removes trailing slash from pathname, to consistently match routes
 */
export const getProceedAction = (state: RootState, pathnameOverwrite?: string | null): ProceedAction | null => {
  const pathname = (pathnameOverwrite || getLocation(state).pathname).replace(/\/$/, '');
  switch (pathname) {
    // destination index
    case r.generalFlow.destination.index():
      return getProceedActionDestinationIndex(state);
    // pickup index
    case r.generalFlow.pickup.index():
      return getProceedActionPickupIndex(state);
    // home situation
    case r.generalFlow.homeSituation.index():
      return getProceedActionHomeSituation(state);
    // auction
    case r.generalFlow.pickup.auction():
      return getProceedActionAuctionType(state);
    // items
    case r.generalFlow.items.index():
      return getProceedActionItems(state);
    // Product payment
    case r.generalFlow.productPayment.index():
      return getProceedActionProductPayment(state);
    case r.generalFlow.productPayment.amount():
      return getIsFlexDates(state) ? { path: r.generalFlow.help.index() } : { path: r.generalFlow.date.index() };
    // date
    case r.generalFlow.date.index():
      return getProceedActionDates(state, StopType.PICKUP);
    case r.generalFlow.date.delivery():
      return getProceedActionDates(state, StopType.DELIVERY);
    // time
    case r.generalFlow.time.index():
      return getProceedActionTime(state, 'pickup');
    case r.generalFlow.time.delivery():
      return getProceedActionTime(state, 'delivery');
    // help
    case r.generalFlow.help.index():
      return getProceedActionHelp(state);
    // floor
    case r.generalFlow.pickup.floor():
      return getProceedActionFloor(state, StopType.PICKUP);
    case r.generalFlow.delivery.floor():
      return getProceedActionFloor(state, StopType.DELIVERY);
    // contact
    case r.generalFlow.contact.index():
      return { path: r.generalFlow.contact.pickup() };
    case r.generalFlow.contact.pickup():
      return { path: r.generalFlow.contact.delivery() };
    case r.generalFlow.contact.delivery():
      return getProceedActionContactDelivery(state);
    // invoice
    case r.generalFlow.invoice.home():
      return getProceedActionInvoices(state);
  }

  return null;
};

const getProceedActionDestinationIndex = (state: RootState): ProceedAction | null => {
  if (getAccountHasBusinessFlow(state)) {
    return { path: r.generalFlow.items.index() };
  }
  return { path: r.generalFlow.pickup.index() };
};

const getProceedActionPickupIndex = (state: RootState): ProceedAction | null => {
  const situation = state?.generalTransport.choices?.situation;
  if (!situation) {
    return {
      missingChoice: 'situation',
    };
  }
  switch (situation) {
    case 'home':
      return { path: r.generalFlow.homeSituation.index() };
    case 'auction':
      return { path: r.generalFlow.pickup.auction() };
    case 'book_a_van':
    case 'store':
      return { path: r.generalFlow.items.index() };
  }
  return null;
};

const getProceedActionHomeSituation = (state: RootState): ProceedAction => {
  const chosen = state?.generalTransport.choices?.home_situation;
  if (!chosen) {
    return {
      missingChoice: 'home_situation',
    };
  }
  return { path: r.generalFlow.items.index() };
};

const getProceedActionAuctionType = (state: RootState): ProceedAction => {
  const auctionType = state?.generalTransport.choices?.auction_type;
  if (!auctionType) {
    return {
      missingChoice: 'auction_type',
    };
  }
  return { path: r.generalFlow.items.index() };
};

const getProceedActionItems = (state: RootState): ProceedAction => {
  const isFlexDates = getIsFlexDates(state);
  const isFlexDatesModalActive = getIsFlexDatesModalActive(state);

  if (isFlexDates && !isFlexDatesModalActive) {
    return { showFlexDatesModal: true };
  }

  const pickupSituation = getPickupSituation(state);
  const homeSituation = state.generalTransport.choices?.home_situation;
  const quote = getQuote(state);
  const isMarketplaceUser = getIsMarketplaceUtmSource(state);
  const hasBusinessFlow = getAccountHasBusinessFlow(state);
  const showProductPayment =
    !hasBusinessFlow &&
    quote?.guaranteed.directly_payable &&
    pickupSituation !== 'auction' &&
    (isMarketplaceUser || ['marktplaats', 'facebook_marketplace', '2dehands'].includes(homeSituation || ''));

  const itemFacts = {
    pickupSituation,
    homeSituation,
    isFlexDates,
    hasBusinessFlow,
    isMarketplaceUser,
    showProductPayment,
  };
  setSentryContext('flow_facts', itemFacts);

  Sentry.setTag('flex_dates', !!isFlexDates);

  if (showProductPayment) {
    return { path: r.generalFlow.productPayment.index() };
  }
  const datesNotAvailable = getIsDateSelectionNotAvailable(state);
  setSentryContext('flow_facts', { datesNotAvailable });
  if (datesNotAvailable || isFlexDates) {
    return { path: r.generalFlow.help.index() };
  }
  return { path: getInitialDatePath(state) };
};

const getProceedActionProductPayment = (state: RootState): ProceedAction => {
  const optIn = getProductPaymentOptIn(state);
  if (optIn) {
    return { path: r.generalFlow.productPayment.amount() };
  }
  if (getIsFlexDates(state)) {
    return { path: r.generalFlow.help.index() };
  }
  return { path: r.generalFlow.date.index() };
};

const getProceedActionDates = (state: RootState, stopType: StopType): ProceedAction => {
  const chosen = state?.generalTransport.choices?.[`${stopType}_date`];
  if (!chosen) {
    return {
      missingChoice: `${stopType}_date`,
    };
  }
  /**
   * When path is pickup AND the customer selected diff delivery date
   * => go to delivery date selection
   */
  if (stopType === StopType.PICKUP && getDifferentDeliveryDate(state)) {
    return { path: r.generalFlow.date.delivery() };
  }
  /**
   * When time selection is available
   * => go to time selection
   */
  const skipPickupTime = getSkipTimeOptions(state, StopType.PICKUP);
  const skipDeliveryTime = getSkipTimeOptions(state, StopType.DELIVERY);

  if (!skipPickupTime || !skipDeliveryTime) {
    return { path: !skipPickupTime ? r.generalFlow.time.index() : r.generalFlow.time.delivery() };
  }
  /**
   * In any other case we are dealing with a situation where time selection is not available.
   * => Inform user
   */
  return { showTimeModal: true };
};

const getProceedActionTime = (state: RootState, stopType: 'pickup' | 'delivery'): ProceedAction => {
  const chosen = state?.generalTransport.choices?.[`${stopType}_time`];
  if (!chosen) {
    return {
      missingChoice: `${stopType}_time`,
    };
  }
  if (stopType === 'pickup') return { path: r.generalFlow.time.delivery() };
  return { path: r.generalFlow.help.index() };
};

const getProceedActionFloor = (state: RootState, stop: StopType): ProceedAction => {
  const val = state?.generalTransport.choices?.[`${stop}_floor`];
  if (!val) {
    return {
      missingChoice: `${stop}_floor`,
    };
  }
  if (stop === StopType.PICKUP) return { path: r.generalFlow.delivery.floor() };
  return { path: getContactStartPath(state) };
};

const getProceedActionHelp = (state: RootState): ProceedAction => {
  if (!isFloorServiceAvailable(state)) {
    return { path: getContactStartPath(state) };
  }
  return { path: r.generalFlow.pickup.floor() };
};

const getProceedActionContactDelivery = (state: RootState): ProceedAction => {
  const situation = getPickupSituation(state);
  const hasBusinessFlow = getAccountHasBusinessFlow(state);
  const isBusinessAndStore = hasBusinessFlow && situation === 'store';
  if (!isBusinessAndStore && ['auction', 'store'].includes(situation || '')) {
    return { path: r.generalFlow.invoice.home() };
  }
  return { path: r.generalFlow.terms.index() };
};

const getProceedActionInvoices = (state: RootState): ProceedAction => {
  const situation = getPickupSituation(state);
  const isRequired = situation === 'auction';
  if (isRequired && !state?.generalTransport.choices?.invoices?.length) {
    return {
      missingChoice: 'invoices',
    };
  }
  return { path: r.generalFlow.terms.index() };
};

export const getContactStartPath = (state: RootState): string => {
  if (isUserLoggedIn(state)) return r.generalFlow.contact.pickup();
  return r.generalFlow.contact.index();
};
